import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Settings,
  BarChart3,
  LineChart,
  FileText,
  Target,
  User,
  ChevronLeft,
  ChevronRight,
  LogOut,
} from 'lucide-react';
import Logo from './Logo';
import { logout, getUserRole, getAllowedPages } from '../services/api';

const Layout = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const location = useLocation();
  const mainRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  React.useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    // Listen on both the main element and window — one of them is the actual scroller
    const handleScroll = () => {
      const scrolled = el.scrollTop > 300 || window.scrollY > 300 || document.documentElement.scrollTop > 300;
      setShowScrollTop(scrolled);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const userRole = getUserRole();
  const allowedPages = getAllowedPages();

  const hasAccess = (pageKey) => {
    if (!pageKey) return true; // No restriction (e.g. Simulator)
    if (allowedPages.includes('all')) return true;
    return allowedPages.includes(pageKey);
  };

  const allNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', pageKey: 'dashboard' },
    { icon: FileText, label: 'Reports', path: '/reports', pageKey: 'reports' },
    { icon: Target, label: 'Strategy', path: '/strategy', pageKey: null },  // everyone sees Strategy
    { icon: BarChart3, label: 'Indexes', path: '/indexes', pageKey: 'indexes' },
    { icon: LineChart, label: 'Simulator', path: '/simulator', pageKey: null },
    { icon: Settings, label: 'Settings', path: '/settings', pageKey: 'settings' },
  ];

  const navItems = allNavItems.filter(item => hasAccess(item.pageKey));

  return (
    <div className="min-h-screen bg-dark-900 flex">
      {/* Sidebar */}
      <div className={`${isCollapsed ? 'w-16' : 'w-72'} bg-gradient-to-b from-dark-800 via-dark-800 to-dark-900 border-r border-dark-600/50 flex flex-col sidebar-fixed sidebar-transition z-30 shadow-xl`}>
        {/* Logo and Toggle */}
        <div className={`${isCollapsed ? 'p-4' : 'p-6'} border-b border-dark-600/30 transition-all duration-300 bg-gradient-to-r from-dark-700/50 to-transparent`}>
          <div className={`flex items-center ${isCollapsed ? 'flex-col space-y-2' : 'justify-between'}`}>
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
              {isCollapsed ? (
                <Logo height={28} showText={false} />
              ) : (
                <div className="flex flex-col">
                  <Logo height={32} />
                  <span className="text-xs text-primary-400 font-medium mt-1 ml-1">Admin Panel</span>
                </div>
              )}
            </div>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 rounded-lg hover:bg-dark-600/50 text-dark-400 hover:text-white transition-all duration-200"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-5">
          <div className={`${isCollapsed ? '' : 'mb-6'}`}>
            {!isCollapsed && (
              <h3 className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-4">Navigation</h3>
            )}
            <ul className="space-y-1">
              {navItems.map((item, index) => {
                const isActive = location.pathname.startsWith(item.path) || (item.path === '/dashboard' && location.pathname === '/');
                return (
                  <li key={index}>
                    <Link
                      to={item.path}
                      className={`group flex items-center ${isCollapsed ? 'justify-center p-3' : 'space-x-3 px-4 py-3'} rounded-lg transition-colors duration-200 ${
                        isActive
                          ? 'bg-primary-600 text-white shadow-lg'
                          : 'text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`}
                      title={isCollapsed ? item.label : ''}
                    >
                      <item.icon
                        size={isCollapsed ? 22 : 20}
                        strokeWidth={2.5}
                        className="flex-shrink-0"
                      />
                      {!isCollapsed && (
                        <span className="font-semibold">{item.label}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>

        {/* User Profile */}
        <div className="p-5 border-t border-dark-600/30 bg-gradient-to-r from-dark-700/30 to-transparent">
          {isCollapsed ? (
            <div className="flex flex-col items-center space-y-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg">
                <User size={18} className="text-white" />
              </div>
              <button
                onClick={logout}
                className="p-2 hover:bg-red-600/50 rounded-lg transition-all duration-200 group"
                title="Logout"
              >
                <LogOut size={16} className="text-dark-400 group-hover:text-white transition-all duration-200" />
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-dark-700/50 rounded-xl p-4 backdrop-blur-sm border border-dark-600/20">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-primary-500/20">
                    <User size={18} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">
                      {localStorage.getItem('adminUsername') || 'Admin User'}
                    </p>
                    <p className="text-xs text-primary-300/80 truncate">{userRole === 'admin' ? 'Administrator' : 'Viewer'}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={logout}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white transition-all duration-200 group border border-red-600/20 hover:border-red-600"
              >
                <LogOut size={16} />
                <span className="font-medium text-sm">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col overflow-hidden main-content-offset ${isCollapsed ? 'ml-16' : 'ml-72'}`}>
        <main ref={mainRef} className="flex-1 overflow-auto p-6 relative">
          {children}
          {/* Scroll-to-top button — inside the scrolling container */}
          {showScrollTop && (
            <button
              onClick={() => { mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="fixed bottom-6 right-6 z-50 w-11 h-11 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-xl flex items-center justify-center transition-all hover:scale-110"
              title="Scroll to top"
            >
              <ChevronRight className="w-5 h-5 -rotate-90" />
            </button>
          )}
        </main>
      </div>
    </div>
  );
};

export default Layout;
